import { ArrowGo, Comment, Market, Pack } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { CircleX } from "lucide-react"

import { AuthButton } from "@/components/auth-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { businessPath, chatGroupsPath, ordersPath } from "@/routes"

interface Product {
  id: number
  name?: string
  title?: string
}
interface OrderItem {
  id: number
  quantity: number
  unit_price: number
  total_price: number
  product: Product
}

interface Order {
  id: number
  order_number: string
  status: string
  created_at: string
  delivery_type?: string
  delivery_address?: {
    line1?: string
    details?: string
    street?: string
    floor?: string
    notes?: string
  }
  delivery_date?: string
  payment_method?: string
  payment_status?: string
  notes?: string
  business: { id: number; business_name: string }
  delivery_neighborhood?: {
    id: number
    name: string
    city?: { id: number; name: string }
  }
  order_items: OrderItem[]
  subtotal: number
  delivery_fee: number
  total: number
}

interface Props {
  order: Order
  can_cancel: boolean
  can_review: boolean
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const statusLabel: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  preparing: "Preparando",
  ready: "Listo",
  delivering: "En camino",
  completed: "Completado",
  cancelled: "Cancelado",
}

const shortOrderRef = (orderNumber: string) => {
  const parts = orderNumber.split("-")
  const last = parts[parts.length - 1] ?? orderNumber
  const short = last.slice(-6)
  return short.toUpperCase()
}

const paymentMethodLabel: Record<string, string> = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
}

const paymentStatusLabel: Record<string, string> = {
  pending: "Pendiente",
  paid: "Pagado",
  refunded: "Reintegrado",
}

export default function OrdersShow({ order, can_cancel }: Props) {
  const isDelivery = (order.delivery_type ?? "delivery") === "delivery"
  const address = order.delivery_address
  const addressLine1 = address?.line1 ?? address?.street
  const addressDetails = address?.details
  const addressFloor = address?.floor
  const hasAddress = Boolean(addressLine1 ?? addressDetails ?? addressFloor)
  const deliveryNeighborhoodLabel = order.delivery_neighborhood?.city?.name
    ? `${order.delivery_neighborhood.name}, ${order.delivery_neighborhood.city.name}`
    : order.delivery_neighborhood?.name

  const handleCancel = () => {
    router.patch(
      `${ordersPath()}/${order.id}`,
      { action_type: "cancel" },
      { preserveScroll: true },
    )
  }

  const handleContactBusiness = () => {
    router.post(
      chatGroupsPath(),
      { business_id: order.business.id },
      {
        preserveScroll: true,
      },
    )
  }
  return (
    <AppLayout>
      <Head title={`Pedido #${shortOrderRef(order.order_number)}`} />

      <div className="mt-4 w-full max-w-4xl">
        <Link
          href={ordersPath()}
          className="text-primary flex flex-wrap items-center gap-2 px-4 text-[13px] font-medium tracking-[0.13px]"
        >
          <div className="flex items-center gap-1">
            <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
            Pedidos
          </div>
        </Link>
        <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
          <Pack className="fill-primary h-8 w-8" />
          Pedido #{shortOrderRef(order.order_number)}
          <Badge>{statusLabel[order.status] ?? order.status}</Badge>
        </CardTitle>

        <CardContent className="text-muted-foreground p-4 text-sm">
          <div className="text-muted-foreground truncate font-mono text-xs">
            {order.order_number}
          </div>
          Pedido realizado el {formatDate(order.created_at)}
        </CardContent>

        <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
          <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
            {isDelivery ? "Envio" : "Retiro en local"}
          </CardTitle>
          <CardContent className="space-y-3 text-sm">
            {isDelivery && deliveryNeighborhoodLabel && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Barrio</span>
                <span className="text-foreground text-right font-medium">
                  {deliveryNeighborhoodLabel}
                </span>
              </div>
            )}

            {isDelivery && hasAddress && (
              <div className="space-y-2">
                {addressLine1 && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Dirección</span>
                    <span className="text-foreground text-right font-medium">
                      {addressLine1}
                    </span>
                  </div>
                )}
                {addressFloor && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Piso</span>
                    <span className="text-foreground text-right font-medium">
                      {addressFloor}
                    </span>
                  </div>
                )}
                {addressDetails && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Detalles</span>
                    <span className="text-foreground text-right font-medium">
                      {addressDetails}
                    </span>
                  </div>
                )}
              </div>
            )}

            {order.notes && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Notas</span>
                <span className="text-foreground text-right font-medium">
                  {order.notes}
                </span>
              </div>
            )}
          </CardContent>
          <div className="pt-3 pr-6 text-sm">
            {order.payment_method && (
              <div className="flex justify-between">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Metodo de pago
                </CardTitle>

                <span className="font-medium">
                  {paymentMethodLabel[order.payment_method] ??
                    order.payment_method}
                </span>
              </div>
            )}
            {order.payment_status && (
              <div className="flex justify-between">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Estado del pago
                </CardTitle>

                <span className="font-medium">
                  {paymentStatusLabel[order.payment_status] ??
                    order.payment_status}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-card p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Market className="h-4 w-4" />
            <Link
              href={businessPath(order.business.id)}
              className="hover:underline"
            >
              {order.business.business_name}
            </Link>
          </CardTitle>
          <CardContent className="space-y-2">
            {order.order_items.map((it) => (
              <div key={it.id} className="flex items-center justify-between">
                <div className="truncate">
                  {it.product.name ?? it.product.title ?? "Producto"} x{" "}
                  {it.quantity}
                </div>
                <div className="font-medium">{formatPrice(it.total_price)}</div>
              </div>
            ))}
            <div className="space-y-1 border-t pt-2 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">
                  {formatPrice(order.subtotal)}
                </span>
              </div>
              {order.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span>Envío</span>
                  <span className="font-medium">
                    {formatPrice(order.delivery_fee)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-base">
                <span>Total</span>
                <span className="font-bold">{formatPrice(order.total)}</span>
              </div>
            </div>
          </CardContent>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 p-4">
          <AuthButton
            variant="outline"
            className="text-primary bg-card flex h-[49px] items-center rounded-[14px] border-0 text-[15px] font-normal tracking-[0.45px]"
            onClick={handleContactBusiness}
            authAction="login"
          >
            <Comment className="fill-primary size-6" />
            Contactar al vendedor
          </AuthButton>
          {can_cancel && (
            <Button
              variant="destructive"
              disabled={!can_cancel}
              onClick={handleCancel}
            >
              <CircleX className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
